/**
 * Embedded diagram round-trip utilities.
 *
 * Mirrors draw.io / Excalidraw behaviour: the exported image carries the
 * source diagram JSON so the image can later be re-imported losslessly.
 *
 * - PNG: a `tEXt` chunk is inserted immediately before the terminating `IEND`
 *   chunk, with a correct CRC32 over the chunk type + data.
 * - SVG: a `<metadata id="graphingv2-diagram">` element carrying the JSON in a
 *   CDATA section.
 *
 * Everything here is pure and unit-testable (no DOM / browser dependencies).
 */

// ---------------------------------------------------------------------------
// CRC32 (standard PNG polynomial 0xEDB88320, table-based)
// ---------------------------------------------------------------------------

const CRC_TABLE: Uint32Array = (() => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[n] = c >>> 0
  }
  return table
})()

/**
 * Compute the CRC32 of a byte range using the standard PNG polynomial.
 */
export function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8)
  }
  return (crc ^ 0xffffffff) >>> 0
}

// ---------------------------------------------------------------------------
// PNG tEXt chunk insert / extract
// ---------------------------------------------------------------------------

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]

/**
 * Encode an ASCII/Latin-1 string to bytes. PNG keywords/text are Latin-1.
 */
function latin1Encode(text: string): Uint8Array {
  const out = new Uint8Array(text.length)
  for (let i = 0; i < text.length; i++) {
    out[i] = text.charCodeAt(i) & 0xff
  }
  return out
}

function latin1Decode(bytes: Uint8Array): string {
  let out = ''
  for (let i = 0; i < bytes.length; i++) {
    out += String.fromCharCode(bytes[i])
  }
  return out
}

function readUint32(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3]) >>>
    0
  )
}

function writeUint32(value: number): Uint8Array {
  return new Uint8Array([
    (value >>> 24) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 8) & 0xff,
    value & 0xff,
  ])
}

/**
 * Build a complete PNG `tEXt` chunk: length + "tEXt" + (keyword\0text) + CRC.
 */
function buildTextChunk(keyword: string, text: string): Uint8Array {
  const typeBytes = latin1Encode('tEXt')
  const keywordBytes = latin1Encode(keyword)
  const textBytes = latin1Encode(text)

  // Chunk data: keyword + null separator + text
  const data = new Uint8Array(keywordBytes.length + 1 + textBytes.length)
  data.set(keywordBytes, 0)
  data[keywordBytes.length] = 0
  data.set(textBytes, keywordBytes.length + 1)

  // CRC is computed over the chunk type + chunk data
  const crcInput = new Uint8Array(typeBytes.length + data.length)
  crcInput.set(typeBytes, 0)
  crcInput.set(data, typeBytes.length)
  const crc = crc32(crcInput)

  const chunk = new Uint8Array(4 + 4 + data.length + 4)
  chunk.set(writeUint32(data.length), 0)
  chunk.set(typeBytes, 4)
  chunk.set(data, 8)
  chunk.set(writeUint32(crc), 8 + data.length)
  return chunk
}

/**
 * Locate the byte offset of the IEND chunk's length field.
 * Walks the chunk list rather than naively searching for "IEND" so we never
 * match the bytes inside chunk data.
 */
function findIendOffset(pngBytes: Uint8Array): number {
  let offset = PNG_SIGNATURE.length
  while (offset + 8 <= pngBytes.length) {
    const length = readUint32(pngBytes, offset)
    const type = latin1Decode(pngBytes.subarray(offset + 4, offset + 8))
    if (type === 'IEND') {
      return offset
    }
    // Advance: length(4) + type(4) + data(length) + crc(4)
    offset += 12 + length
  }
  throw new Error('Invalid PNG: IEND chunk not found')
}

/**
 * Insert a tEXt chunk (keyword/text) immediately before the IEND chunk.
 * Returns a new Uint8Array; the input is not mutated.
 */
export function insertPngTextChunk(
  pngBytes: Uint8Array,
  keyword: string,
  text: string
): Uint8Array {
  const iendOffset = findIendOffset(pngBytes)
  const chunk = buildTextChunk(keyword, text)

  const out = new Uint8Array(pngBytes.length + chunk.length)
  out.set(pngBytes.subarray(0, iendOffset), 0)
  out.set(chunk, iendOffset)
  out.set(pngBytes.subarray(iendOffset), iendOffset + chunk.length)
  return out
}

/**
 * Extract the text payload of the first tEXt chunk matching `keyword`.
 * Returns null if no such chunk exists.
 */
export function extractPngTextChunk(
  pngBytes: Uint8Array,
  keyword: string
): string | null {
  let offset = PNG_SIGNATURE.length
  while (offset + 8 <= pngBytes.length) {
    const length = readUint32(pngBytes, offset)
    const type = latin1Decode(pngBytes.subarray(offset + 4, offset + 8))
    const dataStart = offset + 8
    const dataEnd = dataStart + length

    if (type === 'tEXt' && dataEnd <= pngBytes.length) {
      const data = pngBytes.subarray(dataStart, dataEnd)
      // Find null separator between keyword and text
      let sep = -1
      for (let i = 0; i < data.length; i++) {
        if (data[i] === 0) {
          sep = i
          break
        }
      }
      if (sep !== -1) {
        const chunkKeyword = latin1Decode(data.subarray(0, sep))
        if (chunkKeyword === keyword) {
          return latin1Decode(data.subarray(sep + 1))
        }
      }
    }

    if (type === 'IEND') {
      break
    }
    offset += 12 + length
  }
  return null
}

// ---------------------------------------------------------------------------
// SVG metadata insert / extract
// ---------------------------------------------------------------------------

const SVG_METADATA_ID = 'graphingv2-diagram'

/**
 * Escape a string for safe inclusion inside an XML CDATA section by splitting
 * any occurrences of the CDATA terminator `]]>`.
 */
function escapeCdata(text: string): string {
  return text.split(']]>').join(']]]]><![CDATA[>')
}

function unescapeCdata(text: string): string {
  return text.split(']]]]><![CDATA[>').join(']]>')
}

/**
 * Insert a `<metadata id="graphingv2-diagram">` element (with the JSON wrapped
 * in CDATA) immediately after the opening `<svg ...>` tag. If a metadata
 * element with this id already exists it is replaced.
 */
export function insertSvgMetadata(svg: string, text: string): string {
  const metadata = `<metadata id="${SVG_METADATA_ID}"><![CDATA[${escapeCdata(
    text
  )}]]></metadata>`

  // Remove any existing graphingv2 metadata element first.
  const stripped = stripSvgMetadata(svg)

  const openTagMatch = stripped.match(/<svg\b[^>]*>/i)
  if (!openTagMatch || openTagMatch.index === undefined) {
    // No <svg> tag found; append metadata at the end as a best-effort fallback.
    return stripped + metadata
  }

  const insertAt = openTagMatch.index + openTagMatch[0].length
  return stripped.slice(0, insertAt) + metadata + stripped.slice(insertAt)
}

function stripSvgMetadata(svg: string): string {
  const re = new RegExp(
    `<metadata\\b[^>]*\\bid=["']${SVG_METADATA_ID}["'][^>]*>[\\s\\S]*?</metadata>`,
    'i'
  )
  return svg.replace(re, '')
}

/**
 * Extract the JSON payload from the graphingv2 metadata element.
 * Returns null if not present.
 */
export function extractSvgMetadata(svg: string): string | null {
  const re = new RegExp(
    `<metadata\\b[^>]*\\bid=["']${SVG_METADATA_ID}["'][^>]*>([\\s\\S]*?)</metadata>`,
    'i'
  )
  const match = svg.match(re)
  if (!match) return null

  const inner = match[1]
  // Concatenate the contents of all CDATA sections. The escape routine may
  // split a single logical payload into several CDATA sections in order to
  // smuggle a `]]>` terminator through (`]]>` -> `]]]]><![CDATA[>`).
  const cdataRe = /<!\[CDATA\[([\s\S]*?)\]\]>/g
  let combined = ''
  let cdataMatch: RegExpExecArray | null
  let found = false
  while ((cdataMatch = cdataRe.exec(inner)) !== null) {
    combined += cdataMatch[1]
    found = true
  }
  return found ? combined : unescapeCdata(inner)
}
